<template>
  <div class="xy-count-down" ref="componentRef">
    <p v-if="props.showText">{{ visibleText }}</p>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import moment from 'moment'
import { info, defaultProps } from './config'
import { isTimestamp, convertToUTCMillis } from '@/packages/plugins/dateUtils'
import { subscribeGlobalTimer } from '@/packages/plugins/hook/useGlobalTimer'
import useAppStore from '@/h5/stores/app'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)
const appStore = useAppStore ? useAppStore() : null
const componentRef = ref()
const uuid = ref('')
const displayText = ref('')
let unsubscribeGlobalTimer = null
const isH5 = import.meta.env.VITE_APP_TAG === 'H5'
const ZERO_PARTS = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 }

const pad2 = (num) => String(num).padStart(2, '0')

/** 年月日智能拼接：为 0 不展示；仅日有值时展示「Nd」 */
const formatSmartDateText = (parts) => {
  const { year, month, day } = parts
  const hasYear = year > 0
  const hasMonth = month > 0
  const hasDay = day > 0

  if (!hasYear && !hasMonth && hasDay) {
    return `${day}d`
  }
  if (!hasYear && !hasMonth && !hasDay) {
    return ''
  }
  const dateParts = []
  if (hasYear) dateParts.push(String(year))
  if (hasMonth) dateParts.push(pad2(month))
  if (hasDay) dateParts.push(pad2(day))
  return dateParts.join('-')
}

/** 按格式类型格式化倒计时文本 */
const formatCountdownText = (parts) => {
  const { month, day, hour, minute, second } = parts
  const formatType = props.formatType || 'HH:mm:ss'

  switch (formatType) {
    case 'HH:mm':
      return `${pad2(hour)}:${pad2(minute)}`
    case 'MM-DD':
      return `${pad2(month)}-${pad2(day)}`
    case 'Y-M-D HH:mm:ss': {
      const dateStr = formatSmartDateText(parts)
      const timeStr = `${pad2(hour)}:${pad2(minute)}:${pad2(second)}`
      return dateStr ? `${dateStr} ${timeStr}` : timeStr
    }
    case 'HH:mm:ss':
    default:
      return `${pad2(hour)}:${pad2(minute)}:${pad2(second)}`
  }
}

/** 无目标时间时，按 formatType 展示全 0 */
const getEmptyCountdownText = () => formatCountdownText(ZERO_PARTS)

/** 展示文本 */
const visibleText = computed(() => displayText.value || getEmptyCountdownText())

/** 获取服务器校准后的当前时间戳 */
const getServerTime = () => {
  const timeInfo = appStore?.getPageDataSource('data-source_timeInfo')
  let currentTime = new Date().getTime()
  if (timeInfo && timeInfo.serverTime && timeInfo.localTime) {
    const diffTime = timeInfo.serverTime - timeInfo.localTime
    currentTime = new Date().getTime() + diffTime
  }
  return currentTime
}

/** 将目标时间配置解析为时间戳 */
const parseTargetTimestamp = (targetTime) => {
  if (!targetTime) {
    return null
  }
  const timeStr = String(targetTime).trim()
  if (isTimestamp(timeStr)) {
    return Number(timeStr)
  }
  return convertToUTCMillis(timeStr)
}

/** 根据剩余毫秒计算倒计时分量 */
const getCountdownParts = (remainingMs) => {
  const duration = moment.duration(Math.max(0, remainingMs))
  return {
    year: duration.years(),
    month: duration.months(),
    day: duration.days(),
    hour: duration.hours(),
    minute: duration.minutes(),
    second: duration.seconds()
  }
}

const updateCountdown = () => {
  if (!isH5) {
    return
  }
  const targetTimestamp = parseTargetTimestamp(props.targetTime)
  if (!targetTimestamp) {
    const text = getEmptyCountdownText()
    displayText.value = text
    syncDataSource({ ...ZERO_PARTS, text, isEnded: false })
    return
  }
  const remainingMs = targetTimestamp - getServerTime()
  const parts = getCountdownParts(remainingMs)
  const text = formatCountdownText(parts)
  const isEnded = remainingMs <= 0
  displayText.value = text
  syncDataSource({ ...parts, text, isEnded })
}

const syncDataSource = (data) => {
  if (uuid.value && appStore) {
    appStore.setPageDataSource(uuid.value, data)
  }
}

watch(
  () => [props.targetTime, props.formatType],
  () => {
    if (isH5) {
      updateCountdown()
    }
  }
)

watch(
  () => appStore?.getPageDataSource('data-source_timeInfo'),
  () => {
    if (isH5) {
      console.log('--获取到服务器时间--', appStore?.getPageDataSource('data-source_timeInfo'))
      updateCountdown()
    }
  },
  { deep: true }
)

onMounted(() => {
  nextTick(() => {
    if (componentRef.value) {
      uuid.value = componentRef.value.getAttribute('id') || ''
      if (isH5) {
        updateCountdown()
        unsubscribeGlobalTimer = subscribeGlobalTimer(updateCountdown)
      }
    }
  })
})

onBeforeUnmount(() => {
  if (unsubscribeGlobalTimer) {
    unsubscribeGlobalTimer()
    unsubscribeGlobalTimer = null
  }
})
</script>

<style lang="scss" scoped>
.xy-count-down {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  p {
    margin: 0;
    width: 100%;
    height: auto;
    word-break: break-all;
    white-space: initial;
  }
}
</style>
